import React, { useState } from 'react';
import { Button } from '../../../../components/ui/button';
import {
  DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose
} from '../../../../components/ui/dialog';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Switch } from '../../../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../../../components/ui/select';
import { useToast } from '../../../../hooks/use-toast';
import { updateAdminUser } from '../../../../services/AdminApis';

// This should match the AdminGroup enum in your Python code
const ADMIN_GROUPS = [
  "SuperAdmin", "SalesAdmin", "MarketingAdmin", "SEOUser", "CategoryManager",
  "CXAdmin", "CXAgent", "CXTL", "SalesAgent", "SalesTL", "AffiliateManager", "AffiliateAgent", "SalesContractor",
  "EnterpriseSalesManager"
];

export const EditUserDialog = ({ user, onSuccess, onClose }) => {
  const [firstName, setFirstName] = useState(user.first_name || '');
  const [lastName, setLastName] = useState(user.last_name || '');
  const [group, setGroup] = useState(user.group || '');
  const [isActive, setIsActive] = useState(!!user.is_active);
  const [zohoUserId, setZohoUserId] = useState(user.zoho_user_id || '');
  const [isDefaultOwner, setIsDefaultOwner] = useState(!!user.is_default_owner);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await updateAdminUser(user.id, {
        first_name: firstName || null,
        last_name: lastName || null,
        group,
        is_active: isActive,
        zoho_user_id: zohoUserId || null,
        is_default_owner: isDefaultOwner,
      });
      toast({
        variant: "success",
        title: "Admin Updated",
        description: `${user.email}'s details have been updated.`,
      });
      onSuccess();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Update Failed",
        description: error.response?.data?.detail || "An unexpected error occurred.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-[425px]">
      <form onSubmit={handleSubmit}>
        <DialogHeader>
          <DialogTitle>Edit Admin User</DialogTitle>
          <DialogDescription>
            Update details for {user.email}. Email cannot be changed here since it's used to link this account to Cognito on first login.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="email" className="text-right">Email</Label>
            <Input id="email" value={user.email} disabled className="col-span-3" />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="first_name" className="text-right">First Name</Label>
            <Input
              id="first_name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              className="col-span-3"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="last_name" className="text-right">Last Name</Label>
            <Input
              id="last_name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              className="col-span-3"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="group" className="text-right">Group</Label>
            <Select onValueChange={setGroup} value={group}>
              <SelectTrigger id="group" className="col-span-3">
                <SelectValue placeholder="Select a group" />
              </SelectTrigger>
              <SelectContent>
                {ADMIN_GROUPS.map(g => (
                  <SelectItem key={g} value={g}>{g}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="zoho_user_id" className="text-right">Zoho CRM User ID</Label>
            <Input
              id="zoho_user_id"
              value={zohoUserId}
              onChange={(e) => setZohoUserId(e.target.value)}
              className="col-span-3"
              placeholder="e.g. 986323000000415001"
            />
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="is_active" className="text-right">Active</Label>
            <div className="col-span-3">
              <Switch id="is_active" checked={isActive} onCheckedChange={setIsActive} />
            </div>
          </div>
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="is_default_owner" className="text-right">Default/House Owner</Label>
            <div className="col-span-3">
              <Switch id="is_default_owner" checked={isDefaultOwner} onCheckedChange={setIsDefaultOwner} />
              <p className="text-xs text-muted-foreground mt-1">
                Zoho CRM seller-assignment ownership reverts to this admin when a deal expires. Only one admin should normally have this on.
              </p>
            </div>
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="secondary">Cancel</Button>
          </DialogClose>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
};
